import { hashHistory } from 'react-router';

import { t } from '../services/i18nService';

import aclService from './AclService';
import alertService from './AlertService';
import apiService from './api/ApiService';
import assessmentReader from '../store/readHelpers/AssessmentReader';
import chromeAppService from './ChromeAppService';
import currentlyShownActions from '../actions/CurrentlyShownActions';
import dataCleanUpService from './DataCleanUpService';
import environmentNames from '../environmentNames';
import hypothesisService from './HypothesisService';
import playSequenceService from './PlaySequenceService';
import routeTypes from '../constants/RouteTypes';
import scorableItemNumberingService from './questionView/ScorableItemNumberingService';
import scrollingService from './ScrollingService';
import sessionActions from '../actions/SessionActions';
import sessionConstants from '../constants/SessionConstants';
import sessionReader from '../store/readHelpers/SessionReader';
import settingsReader from '../store/readHelpers/SettingsReader';
import storeManager from '../store/StoreManager';
import textToSpeechService from './TextToSpeechService';
import timeRemainingReader from '../store/readHelpers/TimeReader';
import tocReader from '../store/readHelpers/TocReader';
import utilService from './UtilService';
import domService from './DomService';
import postMessageService from './PostMessageService';
import annotationService from './AnnotationService';
import backgroundAudioService from './BackgroundAudioService';
import messagesActions from '../actions/MessagesActions';
import a11yService from './a11yService';
import remoteOralExamService from './RemoteOralExamService';
import responseReader from '../store/readHelpers/ResponseReader';
import progressBarService from './ProgressBarService';
import readinessCheckService from './ReadinessCheckService';
import preCheckService from './PreCheckService';
import { safeBrowserLinks } from './api/ApiConstants';
import marketplacePropertiesService from './MarketplacePropertiesService';
import scorableItemReader from '../store/readHelpers/ScorableItemReader';
import reloginService from './ReloginService';
import multimediaCanPauseModes from '../components/question/media/multimedia/MultimediaDisplayConstants';
import uiService from './UIService';
import questionSelectionService from './QuestionSelectionService';
import assessmentService from './AssessmentService';

let exitToRoute;
const questionScrollAnimationLength = 300;
const questionScrollwait = 0;
const GAP_MATCH_SCROLL_CONFIG = { block: 'start', inline: 'nearest', behavior: 'smooth' };
const GAP_MATCH_SCROLL_DURATION = 300; // Approximate duration for smooth scroll animation
const SCROLL_RETRY_MAX_COUNT = 20;
const SCROLL_RETRY_INTERVAL_MS = 100;

class navigationService {
    static clearErrorRouting(location) {
        if (typeof location.hash === 'string' && location.hash.includes('/error/')) {
            location.hash = '';
        }
    }

    static exitFromMessages() {
        if (exitToRoute) {
            navigationService.navigateToPath(exitToRoute);
        } else {
            navigationService.navigateToToc();
        }
        a11yService.focusElementWithTimeout('messagesMenuButton', 'optionsMenuButton');
    }

    static exitFromOptions() {
        storeManager.dispatch(sessionActions.toggleInvigilatorAuthorized(false));
        if (exitToRoute) {
            navigationService.navigateToPath(exitToRoute);
        } else if (sessionReader.isParentTestContextOutsideChildTest()) {
            navigationService.navigateToTestOverview();
        } else {
            navigationService.navigateToDeliveringPage();
        }
        a11yService.focusElementWithTimeout('optionsMenuButton');
    }

    static async exitSeb() {
        const quitSeb = () => navigationService.navigateToUrl(safeBrowserLinks.QUIT);

        await dataCleanUpService.deleteAllDataFromAllAssessments(true);

        apiService
            .notifyClientEvent('seb_exit', '', true)
            .then(quitSeb)
            .catch(quitSeb);
    }

    static getPathForQuestion(questionId, sectionId, skipScroll = false, isCscbQuestionSelected = false) {
        const sectId = sectionId || tocReader.getSectionIdForQuestionId(questionId);
        const isCSCBActive = navigationService.isCSCBActive(sectId, isCscbQuestionSelected);
        if (isCSCBActive) {
            return `/inline/${sectId}/cscb`;
        }
        return `/section/${sectId}/question/${questionId + (skipScroll ? '/skipScroll' : '')}`;
    }

    static getPathForScorableItem(sectionId, questionId, scorableItemOrderNumber, skipScroll = false) {
        const sectId = sectionId || tocReader.getSectionIdForQuestionId(questionId);
        const isCSCBActive = navigationService.isCSCBActive();
        if (isCSCBActive) {
            return `/inline/${sectId}/cscb`;
        }
        return `/section/${sectId}/question/${questionId}/scorableItem/${scorableItemOrderNumber + (skipScroll ? '/skipScroll' : '')}`;
    }

    static getCurrentPath() {
        const { hash = '' } = navigationService.getLocation();
        if (hash.indexOf('#') !== 0) {
            return hash;
        }
        return hash.substr(1);
    }

    static getLocation = () => window.location;

    static handleLocationChange(location, params) {
        playSequenceService.cancelSequence();

        const store = storeManager.getStore();

        if (
            timeRemainingReader.timeHasExpired() &&
            !sessionReader.isAutoDeliverHighStakesEnabled() &&
            !navigationService.isPathAllowedAfterTimeout(location.pathname)
        ) {
            if (sessionReader.isParentTestContextOutsideChildTest()) {
                return navigationService.navigateToTestOverview();
            }
            return navigationService.navigateToDeliveringPage();
        } else if (location.pathname === '/' && !settingsReader.showTableOfContents()) {
            return navigationService.navigateToFirstQuestion();
        }

        let newCurrentlyShown;

        const questionId = utilService.getIntOr(params.questionId, undefined);
        const sectionId = utilService.getIntOr(params.sectionId, undefined);
        const scorableItemOrderNumber = utilService.getIntOr(params.scorableItemOrderNumber, undefined);
        let scorableItem = scorableItemOrderNumber;
        if (!scorableItem && questionId) {
            scorableItem = scorableItemReader.getScorableItemsInQuestion(questionId)[0]?.orderNumber;
        }
        const route = location.pathname;

        if (questionId) {
            const routeType = routeTypes.ROUTE_TYPE_QUESTION;
            newCurrentlyShown = {
                routeType,
                sectionId,
                questionId,
                scorableItem,
                route,
                isScorableItemRoute: !!scorableItemOrderNumber,
            };
        } else if (sectionId) {
            const section = assessmentReader.getSectionById(sectionId);
            const routeType = location.pathname.indexOf('/stimulus') !== -1
                ? routeTypes.ROUTE_TYPE_STIMULUS
                : routeTypes.ROUTE_TYPE_SECTION;

            if (routeType === routeTypes.ROUTE_TYPE_SECTION) {
                const firstQuestionId = section.questions.length
                    ? utilService.getIntOr(section.questions[0].contentRevisionId, undefined)
                    : undefined;
                newCurrentlyShown = { routeType, sectionId, questionId: firstQuestionId, scorableItem: undefined, route };
            }
            newCurrentlyShown = { routeType, sectionId, questionId: undefined, scorableItem: undefined, route };
        } else if (location.pathname.length > 1) {
            const arr = location.pathname.split('/');
            const routeType = arr[1];
            newCurrentlyShown = { routeType, sectionId: undefined, questionId: undefined, scorableItem: undefined, route };
        } else if (location.pathname === '/') {
            const routeType = routeTypes.ROUTE_TYPE_TOC;
            newCurrentlyShown = { routeType, sectionId: undefined, questionId: undefined, scorableItem: undefined, route };
        } else {
            newCurrentlyShown = { routeType: '', sectionId: undefined, questionId: undefined, scorableItem: undefined, route: '/' };
        }

        store.dispatch(currentlyShownActions.updateCurrentlyShown(newCurrentlyShown));

        if (domService.windowHasParent()) {
            postMessageService.postTo(window.parent, { ...newCurrentlyShown, type: 'player:navigation' });

            // Notify parent of navigation
            if (newCurrentlyShown.questionId && newCurrentlyShown.sectionId) {
                navigationService.notifyParentOfNavigation(
                    newCurrentlyShown.questionId,
                    newCurrentlyShown.sectionId
                );
            }
        }

        return undefined;
    }

    static isPathAllowedAfterTimeout(path) {
        /**
         * After timeout you must still be allowed to access the options for adding extra time, delivering to file,
         * exiting secure browser, withdrawing or delivering blank. Other options may not be critical to access, but we allow it
         * since it is harmless.
         * @type {string[]}
         */
        const allowedAfterTimeOut = [
            '/auto-submit',
            '/auto-submit-offline',
            '/delivering',
            '/options',
            '/result',
            '/test-overview',
            `/${routeTypes.ROUTE_TYPE_ID_VERIFICATION}`,
            `/${routeTypes.ROUTE_TYPE_ROOM_SCAN}`,
        ];

        return !!allowedAfterTimeOut.find(item => path.indexOf(item) === 0);
    }

    static isFinalStepAvailable() {
        const isCSCBActive = navigationService.isCSCBActive();
        if (isCSCBActive) {
            return false;
        }
        if (sessionReader.isAutoDeliverHighStakesEnabled()) {
            return false;
        }
        if (marketplacePropertiesService.isQuestionFooterEnabled() && sessionReader.isModeQuestion()) {
            return false;
        }
        return aclService.canSeeSummaryPage()
            || aclService.canSeeResultPage();
    }

    static isNavigationLocked() {
        const state = storeManager.getState();
        if (state.session?.navigationLock.enablePauseMode === multimediaCanPauseModes.MODERATE) {
            return false;
        }
        return state.session.navigationLock.isLocked;
    }

    static isOnFinalStepPage() {
        const { currentlyShown } = storeManager.getState();
        return ['delivering', 'result'].indexOf(currentlyShown.routeType) > -1;
    }

    static isOnLastQuestion() {
        const { currentlyShown } = storeManager.getState();
        const lastQuestionLocation = tocReader.getLastQuestionLocation();
        const contentRevisionId = lastQuestionLocation?.contentRevisionId;
        if (currentlyShown.scorableItem) {
            const lastItemInQuestion = scorableItemNumberingService.getLastNavigatableScorableItemInQuestion(contentRevisionId);
            return (!!lastItemInQuestion && lastItemInQuestion.orderNumber === currentlyShown.scorableItem);
        }
        return contentRevisionId !== undefined && contentRevisionId === currentlyShown.questionId;
    }

    static isOnTOCPage() {
        const { currentlyShown } = storeManager.getState();
        return currentlyShown.routeType === routeTypes.ROUTE_TYPE_TOC;
    }

    static isQuestionPage() {
        const { currentlyShown } = storeManager.getState();
        return currentlyShown.routeType === routeTypes.ROUTE_TYPE_QUESTION;
    }

    static isOnAllView() {
        const { currentlyShown } = storeManager.getState();
        return currentlyShown.routeType === routeTypes.ROUTE_TYPE_ALL;
    }

    static async navigateToDashboardTicket(isForbidden = false, isPwaChromeAppExitRequested = false) {
        const isForbiddenValue = typeof isForbidden === 'boolean' ? isForbidden : false;
        const state = storeManager.getState();
        const isOnline = state.api.isOnline;

        if (isOnline && process.env.NODE_ENV !== environmentNames.DEVELOPMENT) {
            if (!isForbiddenValue) {
                await dataCleanUpService.deleteAllDataFromAllAssessments(true);
                if (sessionReader.userHasChromeKiosk()) {
                    chromeAppService.sendClearTestSession();
                }
            }

            const assessmentRunId = sessionReader.getAssessmentRunId(true);
            const academyMarketplaceId = sessionReader.getAcademyMarketplaceId();
            const isAcademyMarketplaceContext = !isNaN(academyMarketplaceId) && academyMarketplaceId > 0;
            let url = isAcademyMarketplaceContext
                ? `/student?marketplaceId=${academyMarketplaceId}`
                : '/student';

            // Append exit parameter if isPwaChromeAppExitRequested is true
            if (isPwaChromeAppExitRequested) {
                url += `${url.includes('?') ? '&' : '?'}pwaAppAction=close_app`;
            }

            // Add fragment after constructing the base URL
            if (!isAcademyMarketplaceContext && isForbiddenValue) {
                window.removeEventListener('beforeunload', uiService.beforeUnloadHandler);
                alert(t('navigation.wrongContextWarning'));
            }
            url += isAcademyMarketplaceContext
                ? `#academyOnDemand/${sessionReader.getUserId()}`
                : `#list/${assessmentRunId}`;
            window.location = url;
            return url; // For test case
        }

        // Return a default value if not online or in development environment
        return null;
    }

    static navigateToDeliveringPage() {
        if (sessionReader.isContextAnyViewDeliveryType()) {
            return false;
        }
        return navigationService.navigateToDelivering();
    }

    static navigateToDelivering() {
        return navigationService.navigateToPath('/delivering');
    }

    static navigateToEmptyQuestionSetPage() {
        return navigationService.navigateToPath('/error/empty-question-set');
    }

    static navigateToEmptyContentBlockPage() {
        return navigationService.navigateToPath('/error/empty-section');
    }

    static navigateToResponseNotProcessedPage() {
        return navigationService.navigateToPath('/error/response-not-processed');
    }


    static navigateToFileDelivered() {
        return navigationService.navigateToPath('/options/file-delivered');
    }

    static navigateToFinalStep() {
        progressBarService.updateProgressValue();

        if (sessionReader.isContextExam() || sessionReader.isContextPreviewAssessmentRun() || sessionReader.isOfflineMode()) {
            return navigationService.navigateToDeliveringPage();
        }

        return navigationService.navigateToResultPage();
    }

    /**
     * Navigates to the TOC page if settings allow it. Otherwise it navigates to the first question.
     */
    static navigateToFirstPage() {
        if (!settingsReader.showTableOfContents()) {
            return navigationService.navigateToFirstQuestion();
        }
        return navigationService.navigateToToc();
    }

    static navigateToFirstQuestion() {
        const questionInfo = tocReader.getFirstQuestionLocation();
        const contentRevisionId = questionInfo ? questionInfo.contentRevisionId : undefined;
        const sectionId = questionInfo ? questionInfo.sectionId : undefined;

        navigationService.notifyParentOfNavigation(contentRevisionId, sectionId);

        if (contentRevisionId && sectionId) {
            let path = navigationService.getPathForQuestion(contentRevisionId, sectionId);
            if (scorableItemNumberingService.useNumberPerInteraction()) {
                const scorableItemOrderNumber = scorableItemNumberingService.getFirstValidOrderNumberInQuestion(contentRevisionId);
                path = navigationService.getPathForScorableItem(sectionId, contentRevisionId, scorableItemOrderNumber);
            }
            return navigationService.navigateToPath(path);
        }

        return false;
    }

    static notifyParentOfNavigation(contentRevisionId, sectionId) {
        if (
            !sessionReader.isModeSection()
            && !sessionReader.isModeQuestionSet()
        ) {
            return;
        }

        const itemId = assessmentService.mapContentRevisionIdToContentId(contentRevisionId, assessmentReader.getAssessment());

        if (itemId === -1) {
            return;
        }

        postMessageService.postTo(window.parent, {
            type: 'player:navigatedToContent',
            contentRevisionId,
            sectionId,
            itemId,
        });
    }

    static navigateToMessages() {
        const store = storeManager.getStore();
        const currentRoute = storeManager.getState().currentlyShown.route;
        if (currentRoute !== undefined && currentRoute.indexOf('/messages') === -1) {
            exitToRoute = currentRoute;
        }
        apiService.fetchMessages().then((result) => {
            store.dispatch(messagesActions.allMessagesLoaded(result.hits));
        });
        return navigationService.navigateToPath('/messages');
    }

    static navigateToTestOverview() {
        return navigationService.navigateToPath('/test-overview');
    }

    static navigateToRoomScan() {
        return navigationService.navigateToPath('/room-scan');
    }

    static navigateToNextStep() {
        const { currentlyShown } = storeManager.getState();
        let locationData;
        progressBarService.updateProgressValue();
        if (currentlyShown.routeType === routeTypes.ROUTE_TYPE_TOC) {
            locationData = tocReader.getFirstQuestionLocation();
        } else if (currentlyShown.routeType === routeTypes.ROUTE_TYPE_STIMULUS) {
            locationData = tocReader.getFirstQuestionInSectionLocation(currentlyShown.sectionId);
        } else if (scorableItemNumberingService.useNumberPerInteraction() && currentlyShown.scorableItem) {
            locationData = scorableItemNumberingService.getNextScorableItem(currentlyShown.questionId, currentlyShown.scorableItem);
            if (locationData) {
                locationData.sectionId = tocReader.getSectionIdForQuestionId(locationData.contentRevisionId);
            }
        } else if (currentlyShown.questionId) {
            locationData = tocReader.getNextQuestionLocation(currentlyShown.questionId);
        }

        if (currentlyShown?.questionId && locationData?.contentRevisionId &&
            reloginService.shouldStoreNextQuestion() &&
            (reloginService.shouldStoreNextQuestionIdIfVerticalLayoutConditions() ||
                reloginService.shouldStoreNextQuestionIdIfHorizontalLayoutConditions())) {
            reloginService.storeNextQuestionIdFlag(currentlyShown.questionId, locationData.contentRevisionId);
        }

        if (!locationData) {
            if (sessionReader.isContextReadinessCheck()) {
                readinessCheckService.setReadinessCheckComplete();
                if (reloginService.isResumedTestWithReloginRules()) {
                    return reloginService.navigateAfterReadinessCheckCompleted();
                }
                return navigationService.navigateToTestOverview();
            }
            if (!sessionReader.isAutoDeliverHighStakesEnabled()) {
                return navigationService.navigateToFinalStep();
            }
            return false;
        }

        if (
            locationData.questionIndex === 0
            && currentlyShown.routeType !== routeTypes.ROUTE_TYPE_STIMULUS
            && sessionReader.isViewMediaPdf()
        ) {
            const section = assessmentReader.getSectionById(locationData.sectionId);
            if (section.stimulusContent) {
                return navigationService.navigateToSectionStimulus(locationData.sectionId);
            }
        }

        if (scorableItemNumberingService.useNumberPerInteraction() && !isNaN(locationData.orderNumber) && !this.isOnTOCPage()) {
            navigationService.scrollToQuestion(
                locationData.sectionId, locationData.contentRevisionId, locationData.orderNumber, questionScrollwait, questionScrollAnimationLength
            );

            // There should be a timeout to execute navigation after scroll event
            // navigationService.scrollToQuestion - this method returns promise, but we can't handle all cases with
            // .then() or finally()
            setTimeout(() => {
                navigationService.navigateToScorableItem(
                    locationData.sectionId, locationData.contentRevisionId, locationData.orderNumber);
            }, questionScrollwait);
            return true;
        }
        return navigationService.navigateToQuestion(locationData.contentRevisionId, locationData.sectionId);
    }

    static navigateToOptions() {
        const currentRoute = storeManager.getState().currentlyShown.route;
        if (currentRoute !== undefined && currentRoute.indexOf('/options') === -1) {
            exitToRoute = currentRoute;
        }
        return navigationService.navigateToPath('/options');
    }

    static navigateToOptionsContrast() {
        return navigationService.navigateToPath('/options/contrast');
    }

    static navigateToOptionsTTS() {
        return navigationService.navigateToPath('/options/tts');
    }

    static navigateToOptionsDeliverBlank() {
        return navigationService.navigateToPath('/options/deliver-blank');
    }

    static navigateToOptionsExitSeb() {
        return navigationService.navigateToPath('/options/exit-seb');
    }

    static navigateToOptionsExitChromeApp() {
        return navigationService.navigateToPath('/options/exit-chrome-app');
    }

    static navigateToOptionsResumePassword() {
        return navigationService.navigateToPath('/options/resume-password');
    }

    static navigateToOptionsFileDelivery() {
        return navigationService.navigateToPath('/options/file-delivery');
    }

    static navigateToOptionsFileDeliveryChromeApp() {
        return navigationService.navigateToPath('/options/file-delivery-chrome-app');
    }

    static navigateToOptionsLanguage() {
        return navigationService.navigateToPath('/options/language');
    }

    static navigateToOptionsAddExtraTime() {
        return navigationService.navigateToPath('/options/add-extra-time');
    }

    static navigateToOptionsHideTimeRemaining() {
        return navigationService.navigateToPath('/options/distraction-free-mode');
    }

    static navigateToOptionsWithdraw() {
        return navigationService.navigateToPath('/options/withdraw');
    }

    static shouldNavigateToEmptyQuestionSet(questionsIndex) {
        return (questionsIndex.itemsOrder.length === 0 && !navigationService.isInsideAuthoringV2());
    }

    static shouldNavigateToEmptyContentBlock(assessment) {
        return (assessment.sections[0]?.objectType === 'content_qti_section' && marketplacePropertiesService.isEnableEmptyCBPreviewMessage());
    }

    static navigateToAutoSubmit() {
        return navigationService.navigateToPath('/auto-submit');
    }

    static isInsideAuthoringV2(pathSegment = '') {
        try {
            const pathname = window?.parent?.document?.defaultView?.location?.pathname;
            return !!pathname && pathname.startsWith(`/author/${pathSegment}`);
        } catch (error) {
            return false;
        }
    }

    static isChangingSectionContext(path) {
        return this.isDeliveringTargetPath(path) || (this.isQuestionTargetPath(path) && this.isSectionChanging(path));
    }

    static isSectionChanging(path) {
        const { currentlyShown } = storeManager.getState();
        return currentlyShown.sectionId !== this.getTargetSectionId(path);
    }

    static getTargetSectionId(path) {
        const sectionIdReg = /\/section\/(\d+)\/.*/;
        return parseInt(path.match(sectionIdReg)?.[1], 10) || null;
    }

    static isDeliveringTargetPath(path) {
        return path.indexOf('/delivering') !== -1;
    }

    static isQuestionTargetPath(path) {
        return path.indexOf('/question') !== -1;
    }

    static navigateToPath(path) {
        if (path === navigationService.getCurrentPath()) {
            return false;
        }

        if (navigationService.isNavigationLocked()) {
            this.handleNavigationLockMessage();

            return false;
        }

        if (sessionReader.isRequiringPasswordForResume() && path.indexOf('/options') === -1) {
            // not storing the routing attempt in order to prevent bypassing the invigilator password dialog for SEB resume
            // this happens at closing the options panel, also when remaining time is zero (see TimeRemainingService.js )

            return false;
        }

        if (timeRemainingReader.timeHasExpired() &&
            !sessionReader.isAutoDeliverHighStakesEnabled() &&
            !navigationService.isPathAllowedAfterTimeout(path)
        ) {
            if (sessionReader.isParentTestContextOutsideChildTest()) {
                navigationService.navigateToTestOverview();
            } else {
                navigationService.navigateToDeliveringPage();
            }
        }

        if (this.isChangingSectionContext(path)) {
            backgroundAudioService.checkAudioStatusOnSectionNavigation();
        }

        textToSpeechService.removeSoundAndClearSelection();

        hashHistory.push(path);
        return true;
    }

    static handleNavigationLockMessage() {
        const lock = sessionReader.getNavigationLock();
        let alertText;

        switch (lock.reason) {
        case sessionConstants.LOCK.FILE_UPLOAD_IN_PROGRESS:
            alertText = t('navigation.navigationIsLockedBecauseAFileIsUploading');
            break;

        case sessionConstants.LOCK.MULTIMEDIA_PLAYING:
            alertText = t('navigation.navigationIsLockedBecauseASoundIsPlaying');
            break;

        case sessionConstants.LOCK.DELIVER_IN_PROGRESS:
            alertText = t('navigation.navigationIsLockedBecauseDeliveringIsInProgress');
            break;

        case sessionConstants.LOCK.EVALUATION_IN_PROGRESS:
            alertText = t('navigation.navigationIsLockedBecauseEvaluationIsInProgress');
            break;

        case sessionConstants.LOCK.TEST_PAUSED:
            alertText = t('navigation.navigationIsLockedBecauseTestIsPaused');
            break;

        default:
            alertText = t('navigation.navigationIsLocked');
        }

        alertService.showAlert(alertText);
    }

    static navigateToUrl(url) {
        location.href = url;
    }

    static navigateToPermissionDenied() {
        if (sessionReader.isAnnotationMode()) {
            hypothesisService.appendDCMIMetaDataTagsAndInitHypothesisIfNeeded();
            annotationService.dispatchNavigationEvent();
        }
        return navigationService.navigateToPath('/error/403');
    }

    static navigateToPermissionDeniedItems() {
        return navigationService.navigateToPath('/error/forbidden-items');
    }

    static navigateToInvalidTabTokenPage() {
        return navigationService.navigateToPath('/error/invalid-tab-token');
    }

    static navigateToPreviousStep() {
        const state = storeManager.getState();
        const { currentlyShown } = state;
        let locationData;

        if (
            currentlyShown.routeType === routeTypes.ROUTE_TYPE_DELIVERING
            || currentlyShown.routeType === routeTypes.ROUTE_TYPE_RESULT
        ) {
            locationData = tocReader.getLastQuestionLocation();
            if (scorableItemNumberingService.useNumberPerInteraction()) {
                const orderNumber = scorableItemNumberingService.getLastValidOrderNumber();
                if (!isNaN(orderNumber)) {
                    navigationService.scrollToQuestion(
                        locationData.sectionId,
                        locationData.contentRevisionId,
                        orderNumber,
                        questionScrollwait,
                        questionScrollAnimationLength
                    );
                    setTimeout(() => {
                        navigationService.navigateToScorableItem(locationData.sectionId, locationData.contentRevisionId, orderNumber);
                    }, questionScrollwait);
                    return true;
                }
            }
            return navigationService.navigateToQuestion(locationData.contentRevisionId, locationData.sectionId);
        } else if (currentlyShown.routeType === routeTypes.ROUTE_TYPE_QUESTION) {
            if (scorableItemNumberingService.useNumberPerInteraction() && currentlyShown.scorableItem) {
                locationData = scorableItemNumberingService.getPreviousScorableItem(currentlyShown.questionId, currentlyShown.scorableItem);
                if (locationData) {
                    locationData.sectionId = tocReader.getSectionIdForQuestionId(locationData.contentRevisionId);
                }
            } else {
                locationData = tocReader.getPreviousQuestionLocation(currentlyShown.questionId);
            }

            if (locationData) {
                if (currentlyShown?.questionId && locationData?.contentRevisionId &&
                    reloginService.shouldStoreNextQuestion() &&
                    (reloginService.shouldStoreNextQuestionIdIfVerticalLayoutConditions(locationData) ||
                        reloginService.shouldStoreNextQuestionIdIfHorizontalLayoutConditions())) {
                    reloginService.storeNextQuestionIdFlag(currentlyShown.questionId, locationData.contentRevisionId);
                }

                if (scorableItemNumberingService.useNumberPerInteraction() && !isNaN(locationData.orderNumber)) {
                    navigationService.scrollToQuestion(
                        locationData.sectionId,
                        locationData.contentRevisionId,
                        locationData.orderNumber,
                        questionScrollwait,
                        questionScrollAnimationLength
                    );
                    setTimeout(() => {
                        navigationService.navigateToScorableItem(
                            locationData.sectionId, locationData.contentRevisionId, locationData.orderNumber);
                    }, questionScrollwait);
                    return true;
                }
                return navigationService.navigateToQuestion(locationData.contentRevisionId, locationData.sectionId);
            }
        }

        return navigationService.navigateToFirstPage();
    }

    static navigateToQuestion(questionId, sectionId, skipScroll = false, isCscbQuestionSelected = false) {
        progressBarService.updateProgressValue();

        if (timeRemainingReader.timeHasExpired() || sessionReader.hasDelivered()) {
            return false;
        }

        if (isNaN(parseInt(questionId, 10)) || isNaN(parseInt(sectionId, 10))) {
            return false;
        }

        return navigationService.navigateToPath(
            navigationService.getPathForQuestion(questionId, sectionId, skipScroll, isCscbQuestionSelected)
        );
    }

    static navigateToScorableItem(sectionId, questionId, scorableItem, skipScroll = false) {
        if (timeRemainingReader.timeHasExpired() || sessionReader.hasDelivered()) {
            return false;
        }

        if (isNaN(parseInt(questionId, 10)) || isNaN(parseInt(sectionId, 10)) || isNaN(parseInt(scorableItem, 10))) {
            return false;
        }

        return navigationService.navigateToPath(
            navigationService.getPathForScorableItem(sectionId, questionId, scorableItem, skipScroll)
        );
    }

    static navigateToResultPage() {
        return navigationService.navigateToPath('/result');
    }

    static navigateToSection(sectionId, postNavigationCallback) {
        const { currentlyShown } = storeManager.getState();
        if (timeRemainingReader.timeHasExpired() || sessionReader.hasDelivered()) {
            return false;
        }

        const section = assessmentReader.getSectionById(sectionId);
        if (!section?.questions?.[0]) {
            return false;
        }
        const firstQuestionInSection = section.questions[0].contentRevisionId;

        if (currentlyShown?.questionId && firstQuestionInSection && reloginService.shouldStoreNextQuestion()) {
            reloginService.storeNextQuestionIdFlag(currentlyShown.questionId, firstQuestionInSection);
        }

        if (postNavigationCallback) {
            postNavigationCallback({ sectionId, contentRevisionId: firstQuestionInSection });
        }

        return navigationService.navigateToPath(
            navigationService.getPathForQuestion(firstQuestionInSection, sectionId)
        );
    }

    static navigateToSectionStimulus(sectionId) {
        return navigationService.navigateToPath(`/section/${sectionId}/stimulus`);
    }

    static isNavigatingAwayFromIncompleteResponsesConfirmed(questionId) {
        if (settingsReader.isWarningNavigatingFromIncompleteResponsesEnabled() && questionId && !this.isOnTOCPage()
                && !responseReader.isQuestionDone(assessmentReader.getQuestion(questionId))
                && !sessionReader.isContextAnyViewDeliveryType()) {
            return confirm(t('navigation.warningNavigatingFromIncompleteResponses'));
        }
        return true;
    }

    static shouldStandaloneTestNavigateToTestOverviewPage() {
        if (!sessionReader.supportsTestOverview()) {
            return false;
        }
        if (sessionReader.getChildAssessmentRunId()) {
            return false;
        }
        if (sessionReader.isTestThatRequiresPreStartStepAndSittingHasAlreadyStarted()) {
            return false;
        }
        return true;
    }

    /**
     * Navigates to the correct startup page if the requested path is not a valid startup page.
     */
    static navigateToStartingPoint(ignoreCurrentRoute = false) {
        const state = storeManager.getState();
        const route = ignoreCurrentRoute ? '/' : state.currentlyShown?.route;

        if (sessionReader.isModeQuestion()) {
            if (!sessionReader.isViewMediaPrintOrPdf()) {
                return navigationService.navigateToFirstQuestion();
            }
        } else if (sessionReader.isRequiringPasswordForResume()) {
            return navigationService.navigateToOptions();
        } else if (sessionReader.isRemoteOralExam()) {
            return navigationService.navigateToRemoteOralStartingPoint();
        } else if (sessionReader.isParentTest() && !sessionReader.getChildAssessmentRunId()) {
            return navigationService.navigateToTestOverview();
        } else if (navigationService.shouldStandaloneTestNavigateToTestOverviewPage()) {
            return navigationService.navigateToTestOverview();
        } else if ((route === undefined || route === '/') && !settingsReader.showTableOfContents()) {
            return navigationService.navigateToFirstQuestion();
        } else if (sessionReader.isContextExam() && (route === undefined || route === '/')) {
            return navigationService.navigateToFirstPage();
        }

        return false;
    }

    static navigateToToc() {
        return navigationService.navigateToPath('/');
    }

    static navigateToZoomFactor() {
        return navigationService.navigateToPath('/options/zoom-factor');
    }

    static navigateToInvigilatorOptions() {
        return navigationService.navigateToPath('/options/invigilator');
    }

    static navigateToTestInstructions() {
        return navigationService.navigateToPath('/options/test-instructions');
    }

    static navigateToRemoteOralStartingPoint() {
        if (!remoteOralExamService.isCandidateJoined()) {
            navigationService.navigateToTestOverview();
            return;
        }

        const { remoteOralExam: { sessionInfo } } = storeManager.getState();
        if (preCheckService.allShownChecksCompleted() && remoteOralExamService.isInProgress(sessionInfo)) {
            remoteOralExamService.scheduleFetchingOfSessionInfo();
            navigationService.navigateToRemoteOral();
        } else {
            navigationService.navigateToTestOverview();
        }
    }

    static navigateToRemoteOral() {
        return navigationService.navigateToPath('/remote-oral');
    }

    static navigateToIdVerification() {
        return navigationService.navigateToPath('/id-verification');
    }

    static resumedTestAfterPassword() {
        const store = storeManager.getStore();
        store.dispatch(sessionActions.setResumedTestAfterPassword(true));
        const didAutoNavigateToLastVisitedPage = reloginService.redirectedResumedTestAfterPasswordInReloginRulesContext();
        if (didAutoNavigateToLastVisitedPage) {
            return;
        }

        if (navigationService.shouldStandaloneTestNavigateToTestOverviewPage()) {
            navigationService.navigateToTestOverview();
        } else {
            navigationService.navigateToFirstPage();
            if (assessmentReader.hasBackgroundAudioResource()) {
                backgroundAudioService.playAudioForNonChildTest();
            }
        }
    }

    static scrollToRubricOrFirstQuestion(questionId, wait, animationLength) {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                let element;
                const sectionRubricElement = document.getElementById('sectionRubric');
                const isRubricInSectionContent = sectionRubricElement ? !!sectionRubricElement.closest('#sectionContent') : false;

                if (sectionRubricElement && isRubricInSectionContent) {
                    element = sectionRubricElement;
                } else {
                    element = document.getElementById(questionId);
                }
                if (element) {
                    resolve(scrollingService.scrollToElement(element, animationLength, 32));
                }
                reject();
            }, wait);
        });
    }

    static scrollToScorableItem(contentRevisionId, orderNumber, questionId, wait, animationLength) {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const scorableItemId = `scorableItem-${contentRevisionId}_${orderNumber}`;
                const element = document.getElementById(scorableItemId);

                if (element) {
                    navigationService.scrollToElement(element, animationLength, resolve);
                    return;
                }

                if (orderNumber) {
                    navigationService.retryFindAndScrollToElement(
                        scorableItemId,
                        questionId,
                        animationLength,
                        resolve,
                        reject
                    );
                } else {
                    reject();
                }
            }, wait);
        });
    }

    static scrollToElement(element, animationLength, resolve) {
        const dataType = element.dataset.type;
        // Gap match elements have data-type="drop"; null or other values are treated as non-gap-match
        const isGapMatchGap = dataType === 'drop';

        if (isGapMatchGap) {
            element.scrollIntoView(GAP_MATCH_SCROLL_CONFIG);
            // Wait for smooth scroll animation to complete before resolving
            setTimeout(() => resolve(), GAP_MATCH_SCROLL_DURATION);
        } else {
            resolve(scrollingService.scrollToElement(element, animationLength, 150));
        }
    }

    static retryFindAndScrollToElement(scorableItemId, questionId, animationLength, resolve, reject) {
        const maxRetries = SCROLL_RETRY_MAX_COUNT;
        const retryInterval = SCROLL_RETRY_INTERVAL_MS;
        let retryCount = 0;

        const attemptFind = () => {
            const element = document.getElementById(scorableItemId);

            if (element) {
                navigationService.scrollToElement(element, animationLength, resolve);
                return;
            }

            if (retryCount < maxRetries) {
                retryCount++;
                setTimeout(attemptFind, retryInterval);
                return;
            }

            navigationService.fallbackToQuestionContainer(questionId, animationLength, resolve, reject);
        };

        attemptFind();
    }

    static fallbackToQuestionContainer(questionId, animationLength, resolve, reject) {
        const questionElement = document.getElementById(questionId);

        if (questionElement) {
            scrollingService.scrollToElement(questionElement, animationLength, 0);
            return resolve();
        }
        return reject();
    }

    static scrollToQuestion(sectionId, contentRevisionId, orderNumber, wait = 10, animationLength) {
        const questionId = `question-${contentRevisionId}`;
        if (orderNumber) {
            const section = assessmentReader.getSectionById(sectionId);
            const isFirstQuestionInSection = section?.questions?.[0]?.contentRevisionId === contentRevisionId;
            const isFirstScorableItemInQuestion = scorableItemNumberingService
                .getFirstScorableItemInQuestion(contentRevisionId)?.orderNumber === orderNumber;
            const element = document.getElementById(`scorableItem-${contentRevisionId}_${orderNumber}`);
            const isGapMatch = element?.dataset.type === 'drop';

            if (isFirstQuestionInSection && isFirstScorableItemInQuestion) {
                return this.scrollToRubricOrFirstQuestion(questionId, wait, animationLength);
            }
            if (isGapMatch || !isFirstScorableItemInQuestion) {
                return this.scrollToScorableItem(contentRevisionId, orderNumber, questionId, wait, animationLength);
            }
        }

        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const question = document.getElementById(questionId);
                if (question) {
                    resolve(scrollingService.scrollToElement(question, animationLength, 0));
                }
                reject();
            }, wait);
        });
    }

    static scrollToTopStimulus(sectionId, wait = 10, animationLength = 500) {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const questionElement = document.getElementById(`stimulus-wrapper-${sectionId}`);
                if (questionElement) {
                    resolve(scrollingService.scrollToElement(questionElement, animationLength, 0));
                }
                reject();
            }, wait);
        });
    }

    static scrollToGapInput = (contentRevisionId, orderNumber) => {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                const element = document.getElementById(`scorableItem-${contentRevisionId}_${orderNumber}`);
                if (element) {
                    resolve(element.scrollIntoView({ block: 'center', inline: 'center' }));
                }
                reject('Element does not exist');
            });
        });
    }

    static exitTest = () => {
        if (sessionReader.userHasLockDown()) {
            if (sessionReader.userHasChromeKiosk()) {
                chromeAppService.exitChromeApp();
                return;
            }

            navigationService.exitSeb();
            return;
        }

        navigationService.navigateToDashboardTicket();
    }

    static isCSCBActive(sectionId, isCscbQuestionSelected = false) {
        const isCSCB = sectionId
            ? questionSelectionService.isCandidateSelectedContentBlockEnabledForSection(sectionId)
            : questionSelectionService.isCandidateSelectedContentBlockEnabled();
        return isCSCB && !isCscbQuestionSelected && !navigationService.isInsideAuthoringV2('author_edit');
    }
}

export default navigationService;
